import React from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  LinearProgress,
  type Theme,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import {
  ChevronRight as ChevronRightIcon,
  Delete as DeleteIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';
import type { Curve, ParsingError } from '../../../interfaces/toolData';
import { CurveGrid } from './CurveGrid';

interface CurveInfo {
  name: string;
  unit: string;
  fileType: string;
}

interface CurveFileData {
  id: string;
  fileName: string;
  fileType: string | null;
  status: 'uploading' | 'fetching' | 'parsing' | 'success' | 'error';
  curves: CurveInfo[];
  error?: ParsingError;
  progress?: number; // 0-100 for upload progress (only for uploading status)
}

interface CurveFileCardProps {
  fileData: CurveFileData;
  isExpanded: boolean;
  onToggle: (id: string) => void;
  onCurveSelect?: (curve: Curve, fileType: string) => void;
  onFileDelete?: (fileId: string) => void;
  draggedCurve: string | null;
  onCurveClick: (curve: CurveInfo, fileType: string) => void;
  onDragStart: (curve: CurveInfo, fileType: string) => void;
  onDragEnd: () => void;
}

const getFileTypeColor = (fileType: string | null, theme: Theme) => {
  if (!fileType) return theme.palette.error.main; // Red for error files
  
  const colors: Record<string, string> = {
    'MICROPULSE_PULSE': theme.palette.success.main,
    'MICROPULSE_ROTGAMMA': theme.palette.warning.main, 
    'MICROPULSE_DEBUG': theme.palette.secondary.main,
    'MICROPULSE_FLOW': theme.palette.primary.main,
    'MICROPULSE_SURVEY': theme.palette.error.main,
    'MICROPULSE_VIBE': theme.palette.text.secondary,
    'MICROPULSE_GAMMA': theme.palette.info.main,
    'MICROPULSE_TEMP': theme.palette.grey[600]
  };
  return colors[fileType] || theme.palette.text.disabled;
};

const getDisplayFileType = (fileType: string | null): string => {
  if (!fileType) return 'ERROR';
  return fileType.replace('MICROPULSE_', '');
};

export const CurveFileCard: React.FC<CurveFileCardProps> = ({
  fileData,
  isExpanded,
  onToggle,
  onCurveSelect,
  onFileDelete,
  draggedCurve,
  onCurveClick,
  onDragStart,
  onDragEnd
}) => {
  const theme = useTheme();

  return (
    <Box key={fileData.id} sx={{ mb: 2 }}>
      {/* File Header */}
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1, 
          mb: 1,
          p: 0.5,
          cursor: 'pointer',
          borderRadius: 1,
          '&:hover': {
            bgcolor: 'action.hover',
            '.delete-button': {
              opacity: 1
            }
          }
        }}
        onClick={() => onToggle(fileData.id)}
      >
        <Tooltip title={isExpanded ? "Collapse file" : "Expand file"}>
          <IconButton 
            size="small" 
            sx={{ 
              transition: 'transform 0.2s',
              transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)'
            }}
          >
            <ChevronRightIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          </IconButton>
        </Tooltip>
        <FileIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
        <Tooltip title={fileData.fileName}>
          <Typography 
            variant="caption" 
            color="text.secondary"
            sx={{ 
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '150px',
              flex: 1
            }}
          >
            {fileData.fileName.replace('MicroPulse_', '').replace('.csv', '')}
          </Typography>
        </Tooltip>
        <Chip 
          label={getDisplayFileType(fileData.fileType)}
          size="small"
          sx={{ 
            height: 18,
            fontSize: '0.6rem',
            bgcolor: getFileTypeColor(fileData.fileType, theme),
            color: 'white',
            fontWeight: 'bold'
          }}
        />
        {onFileDelete && (
          <Tooltip title="Delete file">
            <IconButton
              size="small"
              className="delete-button"
              onClick={(e) => {
                e.stopPropagation();
                onFileDelete(fileData.id);
              }}
              sx={{ 
                ml: 'auto',
                opacity: 0.3,
                color: 'text.secondary',
                transition: 'opacity 0.2s, color 0.2s',
                '&:hover': {
                  opacity: 1,
                  color: 'error.main',
                  bgcolor: 'error.light',
                  transform: 'scale(1.1)'
                }
              }}
            >
              <DeleteIcon sx={{ fontSize: 12 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* File Content - Loading, Error, Empty, or Curves */}
      <Collapse in={isExpanded} timeout={300}>
        <Box sx={{ pl: 2 }}>
          {(fileData.status === 'uploading' || fileData.status === 'fetching' || fileData.status === 'parsing') && (
            // Loading/Upload state with progress bar
            <Box sx={{ p: 2 }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1, 
                mb: 1,
                color: 'text.secondary'
              }}>
                <CircularProgress size={16} />
                <Typography variant="caption">
                  {fileData.status === 'uploading' ? 'Uploading...' : fileData.status === 'fetching' ? 'Fetching file...' : 'Parsing file...'}
                </Typography>
                {fileData.progress !== undefined && (
                  <Typography variant="caption" sx={{ ml: 'auto', fontWeight: 'bold' }}>
                    {Math.round(fileData.progress)}%
                  </Typography>
                )}
              </Box>
              <LinearProgress 
                variant={fileData.progress !== undefined ? 'determinate' : 'indeterminate'}
                value={fileData.progress}
                sx={{ 
                  height: 4, 
                  borderRadius: 2,
                  bgcolor: 'action.hover',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 2
                  }
                }}
              />
            </Box>
          )}
          
          {fileData.status === 'error' && fileData.error && (
            // Error state
            <Box sx={{ width: '100%', p: 1, bgcolor: 'rgba(244, 67, 54, 0.1)', border: '1px solid rgba(244, 67, 54, 0.3)', borderRadius: 1, mb: 1 }}>
              <Typography variant="caption" color="error.main" sx={{ fontWeight: 'bold', display: 'block' }}>
                {fileData.error.message}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'block', mt: 0.5 }}>
                {fileData.error.details}
              </Typography>
              {fileData.error.suggestedAction && (
                <Typography variant="caption" color="info.main" sx={{ fontSize: '0.65rem', display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                  💡 {fileData.error.suggestedAction}
                </Typography>
              )}
            </Box>
          )}
          
          {fileData.status === 'success' && (!fileData.curves || fileData.curves.length === 0) && (
            // Empty state - file parsed successfully but no curves found
            <Box sx={{ width: '100%', p: 1, bgcolor: 'action.hover', borderRadius: 1, mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                No curves found in this file
              </Typography>
            </Box>
          )}
          
          {fileData.status === 'success' && fileData.curves && fileData.curves.length > 0 && (
            // Success state with curves - render the curve grid
            <CurveGrid
              curves={fileData.curves}
              fileType={fileData.fileType || 'unknown'}
              fileId={fileData.id}
              draggedCurve={draggedCurve}
              onCurveClick={onCurveClick}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              onCurveSelect={onCurveSelect}
            />
          )}
        </Box>
      </Collapse>
    </Box>
  );
}; 